import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Edit, Trash2, Flame, Droplet, Snowflake, Clock, PieChart as PieChartIcon, LayoutGrid, List as ListIcon, FormInput, Upload, Download, UserCheck, XCircle, MessageSquare, ClipboardList, Send, FileText } from 'lucide-react';
import LossReasonDialog from '../components/LossReasonDialog';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { toast } from 'sonner';
import { formatDate, getDateInputValue, validateIndianMobile } from '../utils/helpers';
import LeadKanban from '../components/LeadKanban';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { downloadFile } from '../utils/downloadFile';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRODUCT_OPTIONS = [
  'Term Life', 'Endowment', 'ULIP', 'Whole Life', 'Health',
  'Motor', 'Travel', 'Home', 'Personal Accident', 'Commercial',
];

const PIE_COLORS = ['#1E40AF', '#0EA5E9', '#6366F1', '#8B5CF6', '#EC4899', '#F97316', '#10B981', '#059669', '#EAB308', '#64748B', '#94A3B8'];

const daysBetween = (later, earlier) => {
  if (!later || !earlier) return null;
  const a = new Date(later);
  const b = new Date(earlier);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
};

const Leads = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [view, setView] = useState('list'); // 'list' | 'kanban'
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', source: 'referral', status: 'New',
    temperature: 'Warm', expected_premium: '', follow_up_date: '',
    first_call_date: '', product: '', notes: ''
  });
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkUploading, setBulkUploading] = useState(false);
  const [convertingId, setConvertingId] = useState('');
  // Loss-reason picker state — driven by markLeadLost.
  const [lostReasonTarget, setLostReasonTarget] = useState(null);
  const [lostReasonSaving, setLostReasonSaving] = useState(false);
  // Multi-select + bulk action state (Item 11)
  const [selectedIds, setSelectedIds] = useState({});
  const [bulkOpenType, setBulkOpenType] = useState(''); // '', 'whatsapp', 'sms', 'status', 'task'
  const [bulkChannel, setBulkChannel] = useState('whatsapp');
  const [bulkBody, setBulkBody] = useState('');
  const [bulkStatus, setBulkStatus] = useState('Contacted');
  const [bulkTaskTitle, setBulkTaskTitle] = useState('');
  const [bulkTaskDue, setBulkTaskDue] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMsgResult, setBulkMsgResult] = useState(null);

  // Single-lead WhatsApp state (Item 9)
  const [waOpen, setWaOpen] = useState(false);
  const [waLead, setWaLead] = useState(null);
  const [waBody, setWaBody] = useState('');
  const [waSending, setWaSending] = useState(false);

  const openWaForLead = (lead) => {
    setWaLead(lead);
    setWaBody(`Hi ${lead.name}, this is your agent from InsureGrowth. `);
    setWaOpen(true);
  };
  const sendWaToLead = async () => {
    if (!waLead?.phone) { toast.error('No phone on file'); return; }
    if (!waBody.trim()) { toast.error('Message cannot be empty'); return; }
    setWaSending(true);
    try {
      await axios.post(`${API}/messages/send`, { to: waLead.phone, body: waBody, channel: 'whatsapp' }, { withCredentials: true });
      toast.success('WhatsApp sent');
      setWaOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Send failed');
    } finally { setWaSending(false); }
  };

  const selectedLeads = useMemo(() => leads.filter(l => selectedIds[l.lead_id] && l.status !== 'Lost'), [leads, selectedIds]);
  const selectedCount = selectedLeads.length;

  const toggleAllVisible = (checked, visibleList) => {
    const next = { ...selectedIds };
    visibleList.forEach(l => { if (checked) next[l.lead_id] = true; else delete next[l.lead_id]; });
    setSelectedIds(next);
  };

  const runBulkMessage = async () => {
    if (selectedCount === 0) return;
    if (!bulkBody.trim()) { toast.error('Message body cannot be empty'); return; }
    setBulkBusy(true); setBulkMsgResult(null);
    let sent = 0, failed = 0;
    for (const l of selectedLeads) {
      try {
        await axios.post(`${API}/messages/send`, { to: l.phone, body: bulkBody, channel: bulkChannel }, { withCredentials: true });
        sent += 1;
      } catch { failed += 1; }
    }
    setBulkMsgResult({ sent, failed, total: selectedCount });
    setBulkBusy(false);
    if (sent > 0) toast.success(`${sent}/${selectedCount} ${bulkChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} sent`);
    if (failed > 0) toast.warning(`${failed} failed`);
  };

  const runBulkStatusChange = async () => {
    if (selectedCount === 0) return;
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const l of selectedLeads) {
      try {
        await axios.put(`${API}/leads/${l.lead_id}`, { status: bulkStatus }, { withCredentials: true });
        ok += 1;
      } catch { fail += 1; }
    }
    setBulkBusy(false);
    if (ok > 0) toast.success(`Updated ${ok} lead${ok === 1 ? '' : 's'} to ${bulkStatus}`);
    if (fail > 0) toast.warning(`${fail} failed`);
    setBulkOpenType(''); setSelectedIds({}); loadLeads();
  };

  const runBulkCreateTasks = async () => {
    if (selectedCount === 0) return;
    if (!bulkTaskTitle.trim()) { toast.error('Task title required'); return; }
    setBulkBusy(true);
    let ok = 0, fail = 0;
    for (const l of selectedLeads) {
      try {
        await axios.post(`${API}/tasks`, {
          title: `${bulkTaskTitle} · ${l.name}`,
          description: `Follow-up on lead ${l.name} (${l.phone})`,
          due_date: bulkTaskDue || null,
          priority: 'Medium', status: 'Pending',
        }, { withCredentials: true });
        ok += 1;
      } catch { fail += 1; }
    }
    setBulkBusy(false);
    if (ok > 0) toast.success(`Created ${ok} task${ok === 1 ? '' : 's'}`);
    if (fail > 0) toast.warning(`${fail} failed`);
    setBulkOpenType(''); setBulkTaskTitle(''); setBulkTaskDue('');
  };

  const runBulkExportExcel = () => {
    if (selectedCount === 0) return;
    const header = ['Name', 'Phone', 'Email', 'Source', 'Status', 'Temperature', 'Product', 'Expected Premium', 'Follow-up'];
    const rows = selectedLeads.map(l => [l.name, l.phone, l.email || '', l.source, l.status, l.temperature, l.product || '', l.expected_premium || '', l.follow_up_date || '']);
    const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `leads_${new Date().toISOString().slice(0,10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    toast.success(`Exported ${selectedCount} lead${selectedCount === 1 ? '' : 's'}`);
  };

  const convertLead = async (lead) => {
    if (lead.status === 'Converted') return;
    const hasProduct = Boolean(lead.product && (lead.expected_premium || 0) > 0);
    const msg = hasProduct
      ? `Convert "${lead.name}" to a Client and auto-create a ${lead.product} product stub (₹${lead.expected_premium})?`
      : `Convert "${lead.name}" to a Client? No policy stub will be created (no product/premium captured).`;
    if (!window.confirm(msg)) return;
    setConvertingId(lead.lead_id);
    try {
      const { data } = await axios.post(`${API}/leads/${lead.lead_id}/convert`, {}, { withCredentials: true });
      const parts = ['Client created'];
      if (data.reused_existing) parts[0] = 'Linked to existing client (same mobile)';
      if (data.policy_id) parts.push('policy stub added');
      if (data.product_id) parts.push('product stub added');
      toast.success(parts.join(' · '), {
        duration: 6000,
        action: { label: 'Open Client', onClick: () => { window.location.href = `/clients/${data.client_id}`; } },
      });
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Convert failed');
    } finally { setConvertingId(''); }
  };

  const convertLeadFromEdit = async () => {
    if (!editingLead) return;
    // Persist any pending edits before converting so the new client/policy
    // reflects what the agent just typed (product / premium / notes / etc).
    const mv = validateIndianMobile(formData.phone);
    if (!mv.ok) { toast.error(mv.error); return; }
    try {
      const payload = {
        ...formData,
        phone: mv.digits,
        expected_premium: formData.expected_premium === '' ? null : parseFloat(formData.expected_premium),
      };
      await axios.put(`${API}/leads/${editingLead.lead_id}`, payload, { withCredentials: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save lead edits before converting');
      return;
    }
    // Use the latest form values (not the stale editingLead) to pick the right confirm-copy.
    const merged = { ...editingLead, ...formData, phone: mv.digits };
    await convertLead(merged);
    setDialogOpen(false);
    resetForm();
  };

  const downloadLeadsTemplate = async () => {
    const res = await downloadFile('/templates/leads.xlsx', 'leads_template.xlsx');
    if (!res.ok) toast.error(`Could not download template: ${res.error}`);
    else toast.success('Leads template downloaded');
  };

  const uploadLeadsBulk = async () => {
    if (!bulkFile) { toast.error('Choose an Excel file first'); return; }
    setBulkUploading(true); setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      const { data } = await axios.post(`${API}/leads/bulk-upload`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setBulkResult(data);
      if (data.inserted > 0) toast.success(`Imported ${data.inserted} lead${data.inserted === 1 ? '' : 's'}`);
      if (data.skipped > 0) toast.warning(`${data.skipped} row${data.skipped === 1 ? '' : 's'} skipped — see errors below`);
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bulk upload failed');
    } finally { setBulkUploading(false); }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  // Round 44 · Item 3: ?edit={lead_id} deep-link so email/WhatsApp reminders
  // land agents on the exact lead's edit form.
  useEffect(() => {
    const editId = searchParams.get('edit');
    if (!editId || leads.length === 0) return;
    const match = leads.find(l => l.lead_id === editId);
    if (match) {
      openEditDialog(match);
      const next = new URLSearchParams(searchParams);
      next.delete('edit');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leads, searchParams]);

  const loadLeads = async () => {
    try {
      const { data } = await axios.get(`${API}/leads`, { withCredentials: true });
      setLeads(data);
    } catch (error) {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const mv = validateIndianMobile(formData.phone);
    if (!mv.ok) { toast.error(mv.error); return; }
    try {
      const payload = {
        ...formData,
        phone: mv.digits,
        expected_premium: formData.expected_premium === '' ? null : parseFloat(formData.expected_premium),
      };
      if (editingLead) {
        await axios.put(`${API}/leads/${editingLead.lead_id}`, payload, { withCredentials: true });
        toast.success('Lead updated successfully');
      } else {
        await axios.post(`${API}/leads`, payload, { withCredentials: true });
        toast.success('Lead created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadLeads();
    } catch (error) {
      toast.error('Failed to save lead');
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { withCredentials: true });
      toast.success('Lead deleted successfully');
      loadLeads();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  const markLeadLost = (lead) => {
    setLostReasonTarget(lead);
  };

  const submitMarkLeadLost = async (reason, notes) => {
    if (!lostReasonTarget) return;
    setLostReasonSaving(true);
    try {
      await axios.post(`${API}/leads/${lostReasonTarget.lead_id}/mark-lost`, {
        loss_reason: reason || null, loss_notes: notes || null,
      }, { withCredentials: true });
      toast.success('Lead marked as Lost', {
        action: { label: 'Open Lost Leads', onClick: () => { navigate('/lost-leads'); } },
      });
      setLostReasonTarget(null);
      loadLeads();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark lead lost');
    } finally { setLostReasonSaving(false); }
  };

  const openEditDialog = (lead) => {
    setEditingLead(lead);
    setFormData({
      name: lead.name,
      email: lead.email || '',
      phone: lead.phone,
      source: lead.source,
      status: lead.status,
      temperature: lead.temperature,
      expected_premium: lead.expected_premium || 0,
      follow_up_date: getDateInputValue(lead.follow_up_date),
      first_call_date: getDateInputValue(lead.first_call_date),
      product: lead.product || '',
      notes: lead.notes || ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingLead(null);
    setFormData({
      name: '', email: '', phone: '', source: 'referral', status: 'New',
      temperature: 'Warm', expected_premium: 0, follow_up_date: '',
      first_call_date: '', product: '', notes: ''
    });
  };

  const getTemperatureIcon = (temp) => {
    if (temp === 'Hot') return <Flame className="h-4 w-4 text-orange-500" />;
    if (temp === 'Cold') return <Snowflake className="h-4 w-4 text-blue-400" />;
    return <Droplet className="h-4 w-4 text-blue-600" />;
  };

  // Once a lead is marked "Lost" it belongs on the Lost Leads page — hide it from
  // every view on this page (list, kanban, counts, charts) so the pipeline stays clean.
  const visibleLeads = useMemo(() => leads.filter(l => l.status !== 'Lost'), [leads]);

  // Time-to-first-contact: avg days from lead creation to first_call_date
  const ttfcStats = useMemo(() => {
    const measured = [];
    let uncontacted = 0;
    for (const l of visibleLeads) {
      if (l.first_call_date && l.created_at) {
        const d = daysBetween(l.first_call_date, l.created_at);
        if (d !== null && d >= 0) measured.push(d);
      } else {
        uncontacted += 1;
      }
    }
    const avg = measured.length ? measured.reduce((s, n) => s + n, 0) / measured.length : null;
    const sameDay = measured.filter(d => d === 0).length;
    return {
      avg: avg === null ? null : Math.round(avg * 10) / 10,
      measured: measured.length,
      uncontacted,
      sameDay,
      fastest: measured.length ? Math.min(...measured) : null,
      slowest: measured.length ? Math.max(...measured) : null,
    };
  }, [visibleLeads]);

  // Product-mix pie chart
  const productMix = useMemo(() => {
    const counts = new Map();
    for (const l of visibleLeads) {
      const key = l.product || 'Not decided';
      counts.set(key, (counts.get(key) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [visibleLeads]);

  const getStatusColor = (status) => {
    const colors = {
      'New': 'bg-slate-100 text-slate-800',
      'Contacted': 'bg-blue-100 text-blue-800',
      'Interested': 'bg-green-100 text-green-800',
      'Proposal Sent': 'bg-purple-100 text-purple-800',
      'Converted': 'bg-emerald-100 text-emerald-800',
      'Lost': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>;
  }

  return (
    <div className="space-y-6" data-testid="leads-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Leads</h1>
          <p className="text-slate-600 mt-1">Manage your sales pipeline</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate('/leads/forms')} data-testid="open-lead-forms-button">
            <FormInput className="h-4 w-4 mr-1.5" /> Data Collection Forms
          </Button>
          <Button variant="outline" onClick={() => setBulkOpen(true)} data-testid="open-lead-bulk-upload">
            <Upload className="h-4 w-4 mr-1.5" /> Bulk Upload
          </Button>
          <div className="inline-flex rounded-md border border-slate-200 bg-slate-50 p-0.5" data-testid="leads-view-toggle">
            <button
              type="button"
              onClick={() => setView('list')}
              className={`px-2.5 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${view === 'list' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="leads-view-list"
            >
              <ListIcon className="h-3.5 w-3.5" /> List
            </button>
            <button
              type="button"
              onClick={() => setView('kanban')}
              className={`px-2.5 py-1.5 rounded text-xs font-medium flex items-center gap-1 ${view === 'kanban' ? 'bg-white shadow-sm text-blue-800' : 'text-slate-600 hover:text-slate-900'}`}
              data-testid="leads-view-kanban"
            >
              <LayoutGrid className="h-3.5 w-3.5" /> Kanban
            </button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-blue-800 hover:bg-blue-900" data-testid="add-lead-button">
              <Plus className="h-4 w-4 mr-2" /> Add Lead
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingLead ? 'Edit Lead' : 'Add New Lead'}</DialogTitle>
              <DialogDescription>Fill in the lead details below</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Name *</Label>
                  <Input id="name" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required data-testid="lead-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone *</Label>
                  <Input id="phone" inputMode="numeric" maxLength={10} placeholder="10-digit mobile" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value.replace(/\D/g,'').slice(0,10)})} required data-testid="lead-phone-input" />
                  <p className="text-[10px] text-slate-500">Exactly 10 digits, starts with 6-9</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} data-testid="lead-email-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="source">Source</Label>
                  <Select value={formData.source} onValueChange={(value) => setFormData({...formData, source: value})}>
                    <SelectTrigger data-testid="lead-source-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="referral">Referral</SelectItem>
                      <SelectItem value="walk-in">Walk-in</SelectItem>
                      <SelectItem value="online">Online</SelectItem>
                      <SelectItem value="cold call">Cold Call</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({...formData, status: value})}>
                    <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="New">New</SelectItem>
                      <SelectItem value="Contacted">Contacted</SelectItem>
                      <SelectItem value="Interested">Interested</SelectItem>
                      <SelectItem value="Proposal Sent">Proposal Sent</SelectItem>
                      <SelectItem value="Converted">Converted</SelectItem>
                      <SelectItem value="Lost">Lost</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="temperature">Temperature</Label>
                  <Select value={formData.temperature} onValueChange={(value) => setFormData({...formData, temperature: value})}>
                    <SelectTrigger data-testid="lead-temperature-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Hot">Hot</SelectItem>
                      <SelectItem value="Warm">Warm</SelectItem>
                      <SelectItem value="Cold">Cold</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="expected_premium">Expected Premium (₹)</Label>
                  <Input id="expected_premium" type="number" min="0" placeholder="Enter expected premium" value={formData.expected_premium} onChange={(e) => setFormData({...formData, expected_premium: e.target.value})} data-testid="lead-premium-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="product">Product</Label>
                  <Select value={formData.product || '__unset'} onValueChange={(value) => setFormData({...formData, product: value === '__unset' ? '' : value})}>
                    <SelectTrigger data-testid="lead-product-select"><SelectValue placeholder="Select product" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__unset">— Not decided —</SelectItem>
                      {PRODUCT_OPTIONS.map((p) => (
                        <SelectItem key={p} value={p}>{p}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="first_call_date">First Call Date</Label>
                  <Input id="first_call_date" type="date" value={formData.first_call_date} onChange={(e) => setFormData({...formData, first_call_date: e.target.value})} data-testid="lead-first-call-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="follow_up_date">Follow-up Date</Label>
                  <Input id="follow_up_date" type="date" value={formData.follow_up_date} onChange={(e) => setFormData({...formData, follow_up_date: e.target.value})} data-testid="lead-followup-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Input id="notes" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="lead-notes-input" />
              </div>
              <div className="flex justify-between gap-3">
                {editingLead && editingLead.status !== 'Converted' && (
                  <Button
                    type="button"
                    variant="outline"
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                    onClick={() => convertLeadFromEdit()}
                    disabled={convertingId === editingLead.lead_id}
                    data-testid="edit-lead-convert-button"
                  >
                    <UserCheck className="h-4 w-4 mr-1.5" />
                    {convertingId === editingLead.lead_id ? 'Converting…' : 'Convert to Client'}
                  </Button>
                )}
                <div className="flex gap-3 ml-auto">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" className="bg-blue-800 hover:bg-blue-900" data-testid="lead-submit-button">{editingLead ? 'Update' : 'Create'} Lead</Button>
                </div>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="leads-analytics-row">
        <Card className="border-blue-200 shadow-sm" data-testid="ttfc-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-blue-800 mr-2" />
              <CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">Time to First Contact</CardTitle>
            </div>
            <CardDescription>Average days from lead created to your first call</CardDescription>
          </CardHeader>
          <CardContent>
            {ttfcStats.avg === null ? (
              <p className="text-sm text-slate-500 py-4">
                No leads have a First Call Date logged yet. Add one to the next lead you create to start measuring.
              </p>
            ) : (
              <div className="flex items-end gap-6">
                <div>
                  <p className="text-4xl font-bold text-blue-900" data-testid="ttfc-avg-value" style={{ fontFamily: 'Manrope' }}>
                    {ttfcStats.avg}
                  </p>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">avg days</p>
                </div>
                <div className="flex-1 grid grid-cols-2 gap-x-6 gap-y-1 text-sm pb-1">
                  <div><span className="text-slate-500">Measured:</span> <span className="font-semibold text-slate-900">{ttfcStats.measured}</span></div>
                  <div><span className="text-slate-500">Same-day:</span> <span className="font-semibold text-emerald-700">{ttfcStats.sameDay}</span></div>
                  <div><span className="text-slate-500">Fastest:</span> <span className="font-semibold text-slate-900">{ttfcStats.fastest}d</span></div>
                  <div><span className="text-slate-500">Slowest:</span> <span className="font-semibold text-slate-900">{ttfcStats.slowest}d</span></div>
                  {ttfcStats.uncontacted > 0 && (
                    <div className="col-span-2 mt-1 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                      {ttfcStats.uncontacted} lead{ttfcStats.uncontacted === 1 ? '' : 's'} without a First Call Date yet
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm" data-testid="product-mix-widget">
          <CardHeader className="pb-2">
            <div className="flex items-center">
              <PieChartIcon className="h-5 w-5 text-indigo-700 mr-2" />
              <CardTitle style={{ fontFamily: 'Manrope' }} className="text-base">Product Mix</CardTitle>
            </div>
            <CardDescription>What your pipeline is buying interest in</CardDescription>
          </CardHeader>
          <CardContent>
            {productMix.length === 0 ? (
              <p className="text-sm text-slate-500 py-4">No leads yet.</p>
            ) : (
              <div className="h-52" data-testid="product-mix-chart">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={productMix} dataKey="value" nameKey="name" cx="35%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={2}>
                      {productMix.map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value, name) => [`${value} lead${value === 1 ? '' : 's'}`, name]} />
                    <Legend verticalAlign="middle" align="right" layout="vertical" iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>
      </div>


      {/* Bulk-action toolbar (Item 11) — appears once at least one lead is selected. */}
      {selectedCount > 0 && (
        <div className="sticky top-2 z-10 flex flex-wrap items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 shadow-sm" data-testid="leads-bulk-bar">
          <span className="text-sm font-medium text-blue-900">{selectedCount} selected</span>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => { setBulkChannel('whatsapp'); setBulkBody(''); setBulkMsgResult(null); setBulkOpenType('whatsapp'); }} data-testid="leads-bulk-whatsapp" className="border-green-600 text-green-700 hover:bg-green-50">
              <MessageSquare className="h-3.5 w-3.5 mr-1" /> Bulk WhatsApp
            </Button>
            <Button size="sm" variant="outline" onClick={() => { setBulkChannel('sms'); setBulkBody(''); setBulkMsgResult(null); setBulkOpenType('sms'); }} data-testid="leads-bulk-sms">
              <Send className="h-3.5 w-3.5 mr-1" /> Bulk SMS
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkOpenType('task')} data-testid="leads-bulk-task">
              <ClipboardList className="h-3.5 w-3.5 mr-1" /> Create Tasks
            </Button>
            <Button size="sm" variant="outline" onClick={() => setBulkOpenType('status')} data-testid="leads-bulk-status">
              <Edit className="h-3.5 w-3.5 mr-1" /> Change Status
            </Button>
            <Button size="sm" variant="outline" onClick={runBulkExportExcel} data-testid="leads-bulk-export">
              <FileText className="h-3.5 w-3.5 mr-1" /> Export CSV
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelectedIds({})} data-testid="leads-bulk-clear">Clear</Button>
          </div>
        </div>
      )}

      {view === 'kanban' ? (
        <LeadKanban leads={visibleLeads} onLeadUpdated={loadLeads} />
      ) : (
      <Card className="border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle style={{ fontFamily: 'Manrope' }}>
            All Leads ({visibleLeads.length})
            {leads.length !== visibleLeads.length && (
              <span className="ml-2 text-xs font-normal text-slate-500">
                · <button type="button" onClick={() => navigate('/lost-leads')} className="text-red-600 hover:underline" data-testid="open-lost-leads-from-leads">{leads.length - visibleLeads.length} lost — view in Lost Leads</button>
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {visibleLeads.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No active leads. Add your first lead to get started!</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10">
                    <input type="checkbox" checked={visibleLeads.length > 0 && visibleLeads.every(l => selectedIds[l.lead_id])} onChange={(e) => toggleAllVisible(e.target.checked, visibleLeads)} data-testid="leads-select-all" className="h-4 w-4 accent-blue-800" />
                  </TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Temp</TableHead>
                  <TableHead>Follow-up</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleLeads.map((lead) => (
                  <TableRow key={lead.lead_id} data-testid={`lead-row-${lead.lead_id}`}>
                    <TableCell>
                      <input type="checkbox" checked={!!selectedIds[lead.lead_id]} onChange={(e) => setSelectedIds(prev => { const n = { ...prev }; if (e.target.checked) n[lead.lead_id] = true; else delete n[lead.lead_id]; return n; })} data-testid={`lead-select-${lead.lead_id}`} className="h-4 w-4 accent-blue-800" />
                    </TableCell>
                    <TableCell className="font-medium">{lead.name}</TableCell>
                    <TableCell>{lead.phone}</TableCell>
                    <TableCell><Badge variant="outline">{lead.source}</Badge></TableCell>
                    <TableCell><Badge className={getStatusColor(lead.status)}>{lead.status}</Badge></TableCell>
                    <TableCell>{getTemperatureIcon(lead.temperature)}</TableCell>
                    <TableCell>{lead.follow_up_date ? formatDate(lead.follow_up_date) : '-'}</TableCell>
                    <TableCell className="text-right whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => openWaForLead(lead)} data-testid={`lead-wa-${lead.lead_id}`} title="Send WhatsApp" className="border-green-600 text-green-700 hover:bg-green-50 h-7 px-2 mr-1">
                        <MessageSquare className="h-3.5 w-3.5 mr-1" /> WhatsApp
                      </Button>
                      {lead.status !== 'Converted' && (
                        <Button size="sm" variant="outline" onClick={() => convertLead(lead)} data-testid={`convert-lead-${lead.lead_id}`} disabled={convertingId === lead.lead_id} className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 h-7 px-2 mr-1">
                          <UserCheck className="h-3.5 w-3.5 mr-1" /> Convert
                        </Button>
                      )}
                      {lead.status !== 'Converted' && lead.status !== 'Lost' && (
                        <Button size="sm" variant="outline" onClick={() => markLeadLost(lead)} data-testid={`mark-lead-lost-${lead.lead_id}`} className="border-red-500 text-red-700 hover:bg-red-50 h-7 px-2 mr-1">
                          <XCircle className="h-3.5 w-3.5 mr-1" /> Lost
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openEditDialog(lead)} data-testid={`edit-lead-${lead.lead_id}`} className="h-7 px-2 mr-1">
                        <Edit className="h-3.5 w-3.5 mr-1" /> Edit
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleDelete(lead.lead_id)} data-testid={`delete-lead-${lead.lead_id}`} className="border-red-500 text-red-700 hover:bg-red-50 h-7 px-2">
                        <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      )}

      {/* Bulk-upload dialog */}
      <Dialog open={bulkOpen} onOpenChange={(o) => { setBulkOpen(o); if (!o) { setBulkFile(null); setBulkResult(null); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Bulk upload leads from Excel</DialogTitle>
            <DialogDescription>Upload up to 500 leads at once. Duplicates on phone number are skipped.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex justify-between items-center rounded-md border border-slate-200 bg-slate-50 p-2 text-xs">
              <span>Not sure of the columns? Get a starter template.</span>
              <Button type="button" size="sm" variant="ghost" onClick={downloadLeadsTemplate} data-testid="download-leads-template">
                <Download className="h-3.5 w-3.5 mr-1" /> Download Template
              </Button>
            </div>
            <Input type="file" accept=".xlsx,.xls" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} data-testid="leads-bulk-file-input" />
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setBulkOpen(false)}>Cancel</Button>
              <Button type="button" onClick={uploadLeadsBulk} disabled={bulkUploading || !bulkFile} className="bg-blue-800 hover:bg-blue-900" data-testid="leads-bulk-submit">
                {bulkUploading ? 'Uploading…' : 'Import Leads'}
              </Button>
            </div>
            {bulkResult && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs space-y-1" data-testid="leads-bulk-result">
                <p><strong className="text-emerald-700">Inserted:</strong> {bulkResult.inserted}</p>
                <p><strong className="text-amber-700">Skipped:</strong> {bulkResult.skipped}</p>
                <p><strong>Total rows:</strong> {bulkResult.total_rows}</p>
                {bulkResult.errors?.length > 0 && (
                  <div>
                    <p className="font-semibold mt-1">Errors:</p>
                    <ul className="max-h-32 overflow-y-auto text-red-700">
                      {bulkResult.errors.slice(0, 15).map((e, i) => <li key={i}>• {e}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Loss-reason picker — replaces the free-text prompt so the Analysis report can cluster reasons cleanly. */}
      <LossReasonDialog
        open={!!lostReasonTarget}
        title={lostReasonTarget ? `Mark "${lostReasonTarget.name}" as Lost` : ''}
        description="Pick the closest reason so it clusters on the Lost Leads Analysis chart."
        confirming={lostReasonSaving}
        onCancel={() => setLostReasonTarget(null)}
        onConfirm={submitMarkLeadLost}
      />

      {/* Single-lead WhatsApp dialog (Item 9) */}
      <Dialog open={waOpen} onOpenChange={setWaOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>WhatsApp to {waLead?.name}</DialogTitle>
            <DialogDescription>Sending to {waLead?.phone}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <textarea value={waBody} onChange={(e) => setWaBody(e.target.value)} rows={5} maxLength={1500} className="w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="lead-wa-body" />
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setWaOpen(false)}>Cancel</Button>
              <Button onClick={sendWaToLead} disabled={waSending} className="bg-green-600 hover:bg-green-700" data-testid="lead-wa-send">
                {waSending ? 'Sending…' : 'Send WhatsApp'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk WhatsApp / SMS dialog (Item 10) */}
      <Dialog open={bulkOpenType === 'whatsapp' || bulkOpenType === 'sms'} onOpenChange={(o) => { if (!o) setBulkOpenType(''); }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Bulk {bulkChannel === 'whatsapp' ? 'WhatsApp' : 'SMS'} · {selectedCount} lead{selectedCount === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>Same message will be sent to all selected leads.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <textarea value={bulkBody} onChange={(e) => setBulkBody(e.target.value)} rows={5} maxLength={1500} placeholder="Type your message…" className="w-full px-3 py-2 border border-slate-300 rounded-md" data-testid="leads-bulk-body" />
            {bulkMsgResult && <p className="text-sm text-slate-700">Result: {bulkMsgResult.sent}/{bulkMsgResult.total} sent · {bulkMsgResult.failed} failed</p>}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpenType('')}>Close</Button>
              <Button onClick={runBulkMessage} disabled={bulkBusy || !bulkBody.trim()} className="bg-blue-800 hover:bg-blue-900" data-testid="leads-bulk-send">
                {bulkBusy ? 'Sending…' : `Send to ${selectedCount}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk status change (Item 11) */}
      <Dialog open={bulkOpenType === 'status'} onOpenChange={(o) => { if (!o) setBulkOpenType(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Change status for {selectedCount} lead{selectedCount === 1 ? '' : 's'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Select value={bulkStatus} onValueChange={setBulkStatus}>
              <SelectTrigger data-testid="leads-bulk-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {['New', 'Contacted', 'Interested', 'Proposal Sent', 'Converted'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpenType('')}>Cancel</Button>
              <Button onClick={runBulkStatusChange} disabled={bulkBusy} className="bg-blue-800 hover:bg-blue-900" data-testid="leads-bulk-status-apply">
                {bulkBusy ? 'Updating…' : 'Apply'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk task creation (Item 11) */}
      <Dialog open={bulkOpenType === 'task'} onOpenChange={(o) => { if (!o) setBulkOpenType(''); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create task for {selectedCount} lead{selectedCount === 1 ? '' : 's'}</DialogTitle>
            <DialogDescription>One task per lead. Client name is appended to the title.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label className="text-xs">Task Title *</Label>
              <Input value={bulkTaskTitle} onChange={(e) => setBulkTaskTitle(e.target.value)} placeholder="e.g. Send proposal PDF" data-testid="leads-bulk-task-title" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Due Date</Label>
              <Input type="date" value={bulkTaskDue} onChange={(e) => setBulkTaskDue(e.target.value)} data-testid="leads-bulk-task-due" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setBulkOpenType('')}>Cancel</Button>
              <Button onClick={runBulkCreateTasks} disabled={bulkBusy || !bulkTaskTitle.trim()} className="bg-blue-800 hover:bg-blue-900" data-testid="leads-bulk-task-apply">
                {bulkBusy ? 'Creating…' : `Create ${selectedCount} task${selectedCount === 1 ? '' : 's'}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Leads;
